/**
 * Tiny canvas confetti — palette-tinted, no deps. Fires once and cleans itself
 * up. Designed for the RSVP success moment.
 */

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  size: number;
  colour: string;
  life: number;
  maxLife: number;
}

const PALETTE = [
  "#b97961", // terracotta
  "#d6997f", // coral peach
  "#c29c95", // blush mauve
  "#7d8867", // deep sage
  "#bac4ad", // light sage
  "#ece3d2", // warm cream
  "#f5efe5", // cream
];

export function confettiBurst(
  origin: HTMLElement,
  opts: { count?: number; durationMs?: number } = {},
): void {
  const count = opts.count ?? 90;
  const duration = opts.durationMs ?? 1800;

  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "position:fixed;inset:0;pointer-events:none;z-index:120;";
  document.body.appendChild(canvas);

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const sizeCanvas = () => {
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
  };
  sizeCanvas();

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }
  ctx.scale(dpr, dpr);

  const rect = origin.getBoundingClientRect();
  const ox = rect.left + rect.width / 2;
  const oy = rect.top + rect.height / 2;

  const particles: Particle[] = [];
  for (let i = 0; i < count; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
    const speed = 6 + Math.random() * 8;
    particles.push({
      x: ox,
      y: oy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      rot: Math.random() * Math.PI * 2,
      vrot: (Math.random() - 0.5) * 0.4,
      size: 6 + Math.random() * 8,
      colour: PALETTE[Math.floor(Math.random() * PALETTE.length)] ?? "#b97961",
      life: 0,
      maxLife: duration * (0.6 + Math.random() * 0.4),
    });
  }

  const start = performance.now();
  const gravity = 0.32;
  const drag = 0.992;

  function frame(now: number) {
    const t = now - start;
    if (t > duration + 600) {
      canvas.remove();
      return;
    }
    ctx!.clearRect(0, 0, window.innerWidth, window.innerHeight);

    for (const p of particles) {
      p.life += 16;
      const lifeRatio = p.life / p.maxLife;
      if (lifeRatio >= 1) continue;

      p.vy += gravity;
      p.vx *= drag;
      p.vy *= drag;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vrot;

      const alpha = 1 - lifeRatio;
      ctx!.save();
      ctx!.translate(p.x, p.y);
      ctx!.rotate(p.rot);
      ctx!.globalAlpha = alpha;
      ctx!.fillStyle = p.colour;
      // Confetti shape: small rounded rectangle (paper flake)
      const w = p.size;
      const h = p.size * 0.42;
      ctx!.fillRect(-w / 2, -h / 2, w, h);
      ctx!.restore();
    }
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}
